import React, { useEffect, useState } from "react";
import { ShieldCheck, UserPlus, RefreshCw, Trash2, Copy, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const ModeratorsTab = () => {
  const { t } = useI18n();
  const [moderators, setModerators] = useState([]);
  const [form, setForm] = useState({ email: "", full_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/moderators");
      setModerators(data.items || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(null);
    try {
      const { data } = await api.post("/admin/moderators", form);
      setSuccess({ email: data.email, full_name: data.full_name });
      setForm({ email: "", full_name: "" });
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async (uid) => {
    try {
      await api.post(`/admin/onboard/${uid}/resend-email`);
      alert("Correo de acceso reenviado.");
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const remove = async (uid) => {
    if (!window.confirm("¿Eliminar este moderador? Perderá acceso al panel de inmediato.")) return;
    try {
      await api.delete(`/admin/moderators/${uid}`);
      load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("clipboard failed", err);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-moderators-tab">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-11 h-11 rounded-full bg-teal/10 text-teal flex items-center justify-center">
          <ShieldCheck size={20} />
        </span>
        <div>
          <p className="eyebrow text-orange">{t.admin.tabs.moderators}</p>
          <h3 className="font-display text-2xl text-teal-deep leading-tight">{t.onboard?.modsTitle || "Equipo de moderadores"}</h3>
          <p className="text-xs text-teal-soft mt-1">{t.onboard?.modsSubtitle || "Gestiona quiénes pueden inscribir usuarios y ayudarte con el panel."}</p>
        </div>
      </div>

      {/* Create moderator */}
      <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 items-end bg-cream/50 border border-gray-200 rounded-2xl p-4" data-testid="moderators-create-form">
        <div>
          <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">Correo</label>
          <input
            type="email"
            required
            className="field-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="moderador@correo.com"
            data-testid="moderators-email"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">Nombre completo</label>
          <input
            required
            minLength={2}
            className="field-input"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            placeholder="Ej: María López"
            data-testid="moderators-fullname"
          />
        </div>
        <button type="submit" disabled={loading} className="btn-orange justify-center disabled:opacity-60" data-testid="moderators-submit">
          <UserPlus size={14} /> {loading ? "Creando…" : "Añadir moderador"}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm mt-3" data-testid="moderators-error">{error}</p>}

      {success && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3" data-testid="moderators-success">
          <CheckCircle2 className="text-green-600 shrink-0 mt-0.5" size={18} />
          <div className="flex-1 min-w-0">
            <div className="text-teal-deep font-semibold">Moderador creado.</div>
            <div className="text-sm text-teal-soft">
              Se envió un correo a <b>{success.email}</b> con el enlace para que cree su propia contraseña.
            </div>
            <button
              type="button"
              onClick={() => copy(success.email)}
              className="mt-2 text-xs text-teal font-bold hover:underline inline-flex items-center gap-1"
            >
              <Copy size={12} /> Copiar correo
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="mt-6 overflow-hidden border border-gray-200 rounded-2xl">
        <div className="px-4 py-3 bg-cream/60 border-b border-gray-200 text-sm text-teal-deep font-semibold" data-testid="moderators-total">
          {moderators.length} {moderators.length === 1 ? "moderador" : "moderadores"}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="moderators-table">
            <thead className="bg-cream border-b border-gray-200">
              <tr className="text-left text-teal">
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Nombre</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Correo</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Fecha</th>
                <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {moderators.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-teal-soft" colSpan={4}>
                    Todavía no hay moderadores. Añade el primero con el formulario de arriba.
                  </td>
                </tr>
              ) : (
                moderators.map((m) => (
                  <tr key={m.id} className="border-b border-gray-100 hover:bg-cream/40" data-testid={`moderators-row-${m.id}`}>
                    <td className="px-4 py-3 font-semibold text-teal-deep">{m.full_name || "—"}</td>
                    <td className="px-4 py-3 text-teal-soft">{m.email}</td>
                    <td className="px-4 py-3 text-teal-soft text-xs">{(m.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          onClick={() => resendEmail(m.id)}
                          title="Reenviar correo de acceso"
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-teal/10 text-teal hover:bg-teal hover:text-white rounded-full px-3 py-1.5 transition-colors"
                          data-testid={`moderators-resend-${m.id}`}
                        >
                          <RefreshCw size={11} /> Reenviar
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(m.id)}
                          title="Eliminar moderador"
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-full px-3 py-1.5 transition-colors"
                          data-testid={`moderators-delete-${m.id}`}
                        >
                          <Trash2 size={11} /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModeratorsTab;
